import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { RetrievalStrategy, Station, StationType, TransportCreationProfile, TransportCreationProfileToStation } from '../../model/station.model';
import { SelectFieldOption, TranslationService } from '@sdbn/common';
import { NgForm, ControlContainer, FormGroup, FormControl } from '@angular/forms';
import { StructureSelection } from 'src/tmc-yard/common/structure-selection/structure-selection.model';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { StructureSelectionService } from 'src/tmc-yard/common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { TableSelectionDialogComponent, TableSelectionDialogData } from 'src/tmc-common/components/table-selection-dialog/table-selection-dialog.component';
import { DialogLayout } from 'src/app/shared/dialog-layout/dialog-layout';
import { TmcTableModel } from '@sdbn/appclient';
import { Row } from '@sdbn/appclient';
import { SelectionType } from '@sdbn/appclient';
import { ColumnType } from '@sdbn/appclient';
import { StationService } from '../../service/station.service';

@Component({
  selector: 'app-station-details',
  templateUrl: './station-details.component.html',
  styleUrls: ['./station-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class StationDetailsComponent implements OnInit, OnDestroy {

  possibleRetrievalStrategies = [
      new SelectFieldOption({label : '', value: null }),
      new SelectFieldOption({labelDictionaryKey : 'tmc-yard.common.enum.retrievalStrategy.FIFO', value: RetrievalStrategy.FIFO }),
      new SelectFieldOption({ labelDictionaryKey: 'tmc-yard.common.enum.retrievalStrategy.LIFO', value: RetrievalStrategy.LIFO })];

  tmcContextSubscription: Subscription;

  bufferZoneName: string;
  station: Station;


  @Input() form: NgForm;
  @Input("station") set setStation(station: Station) {
    this.station = station;
    this.updateBufferZoneName();
  }
  @Input() isLoading: boolean;

  // keys for type options
  keys = Object.keys;

  stationTypes = StationType;

  get componentsForPlannedAssignmentVisible(): boolean {
    return this.station?.plannedTransportAssignmentPossible;
  }


  constructor(
    public translationService: TranslationService,
    private tmcContextService: TmcContextService,
    private structureSelectionService: StructureSelectionService,
    private dialog: MatDialog,
    private stationService: StationService
  ) {
    this.tmcContextSubscription = this.tmcContextService.updated.subscribe(() => this.updateBufferZoneName());
  }

  ngOnInit() {

  }

  ngOnDestroy(): void {
    if (this.tmcContextSubscription) {
      this.tmcContextSubscription.unsubscribe();
    }
  }

  assignBufferZone() {

    const structureSelection = new StructureSelection();

    structureSelection.stationId = this.station.bufferZoneId;

    this.structureSelectionService.selectWaitingArea(structureSelection).subscribe(
      value => {

        if (value != null) {
          const selection: StructureSelection = value;
          if (selection.stationId != null) {
            this.station.bufferZoneId = selection.stationId;
            this.updateBufferZoneName();
            this.markFormControlAsDirty('bufferZoneId');
          }
        }
      }
    );
  }

  private updateBufferZoneName() {
    if (this.station?.bufferZoneId != null) {
      const station = this.tmcContextService.findStationById(this.station.bufferZoneId);
      this.bufferZoneName = station?.name;
    } else {
      this.bufferZoneName = "";
    }
  }

  private markFormControlAsDirty(fcName: string) {
    if (this.form.form.controls[fcName]) {
      this.form.form.controls[fcName].markAsDirty();
      this.form.form.controls[fcName].markAsTouched();
      this.form.form.controls[fcName].updateValueAndValidity();
    }
  }

  public removeTransportCreationProfile(profile: TransportCreationProfileToStation) {

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.ui.admin.stations.transport-creation-profiles-confirm-remove-title',
      messageKey: 'tmc-yard.ui.admin.stations.transport-creation-profiles-confirm-remove-description',
      confirm: () => {
        if (profile.id) {
          this.station.deletedTransportCreationProfiles.push(profile);
        }

        const index = this.station.transportCreationProfiles.findIndex(p => p.id === profile.id);
        if (index > -1) {
          this.station.transportCreationProfiles.splice(index, 1);
        }

        this.updatePositionsIndex();
        this.form.form.markAsDirty();
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public dropProfile(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.station.transportCreationProfiles, event.previousIndex, event.currentIndex);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  public addTransportCreationProfile() {
    this.stationService.getTransportCreationProfiles().subscribe(
      profiles => this.openTransportCreationProfileSelectionDialog(profiles)
    );
  }

  private openTransportCreationProfileSelectionDialog(availableTransportCreationProfiles: Array<TransportCreationProfile>) {

    const assignedProfileIds = this.station.transportCreationProfiles.map(profile => profile.id);
    const assignableProfiles = availableTransportCreationProfiles.filter(profile => assignedProfileIds.indexOf(profile.id) === -1);

    const tableSelectionDialogData: TableSelectionDialogData = {
      title: this.translationService.translate('tmc-yard.ui.admin.stations.transport-creation-profiles-selection-title'),
      tableModel: this.createTableModelForTransportCreationProfileSelectionDialog(assignableProfiles),
      confirmCallback: (tableModel: TmcTableModel) => {
        this.addTransportCreationProfileToStation(tableModel.selectedRows);
      }
    };

    this.dialog.open(TableSelectionDialogComponent, {
      data: tableSelectionDialogData,
      panelClass: DialogLayout.panelClass
    });
  }

  private addTransportCreationProfileToStation(selectedRows: Row[]) {
    const selectedProfiles = this.convertRowsToTransportCreationProfileToStation(selectedRows);
    this.station.transportCreationProfiles.push(...selectedProfiles);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  private updatePositionsIndex() {
    for (let i = 0; i < this.station.transportCreationProfiles.length; i++) {
      this.station.transportCreationProfiles[i].index = i;
    }
  }

  private createTableModelForTransportCreationProfileSelectionDialog(assignableTransportCreationProfiles: TransportCreationProfile[]): TmcTableModel {
    const tableModel = new TmcTableModel();
    tableModel.selectionType = SelectionType.MULTIPLE;
    tableModel.sortable = true;
    tableModel.filterable = true;
    tableModel.sortColumn = 'name';
    tableModel.sortDirection = 'asc';
    tableModel.columnDefinitions = [
      { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: 'tmc-yard.entity.transport-creation-profile.name' }
    ];
    tableModel.setRows(this.convertTransportCreationProfileToRows(assignableTransportCreationProfiles));
    return tableModel;
  }


  private convertTransportCreationProfileToRows(transportCreationProfiles: TransportCreationProfile[]): Row[] {
    const rows: Row[] = [];
    for (const profile of transportCreationProfiles) {
      const row: Row = new Row();
      row.id = profile.id + '';
      row.values = {
        id: profile.id,
        name: profile.name
      };

      rows.push(row);
    }
    return rows;
  }

  private convertRowsToTransportCreationProfileToStation(selectedRows: Row[]): TransportCreationProfileToStation[] {
    return selectedRows.map(row => {
      const profile: TransportCreationProfileToStation = new TransportCreationProfileToStation();
      profile.id = null;
      profile.transportCreationProfileId  = row.values.id;
      profile.name = row.values.displayName;
      return profile;
    });
  }

}
